import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SortingRule } from "react-table";
import { Col, Row } from "reactstrap";
import ButtonComponent from "../../components/ButtonComponent";
import InputComponent from "../../components/InputComponent";
import Layout from "../../components/Layout/Layout";
import TableComponent, { OperationsCell } from "../../components/TableComponent";
import Routes from "../../helpers/Routes";
import Toast from "../../helpers/Toast";
import { Utilities } from "../../helpers/Utilities";
import { Parameter } from "../../models/Parameter";
import { DeliveryNoteService } from "../../services/DeliveryNoteService";
import { DeliveryNoteSearchResultDTO } from "../../models/DeliveryNote/DeliveryNoteSearchResultDTO";
import SelectComponent from "../../components/SelectComponent";
import { SelectItemsProps } from "../client/ClientEdit";
import { AssistanceService } from "../../services/AssistanceService";

export default function DeliveryNoteIndex() {
    const intl = useIntl();
    //Declaração de outros estados e variáveis importantes
    const navigate = useNavigate();
    const deliveryNoteService = new DeliveryNoteService();
    const assistanceService = new AssistanceService();
    const pageSizes: number[] = [5, 15, 30];
    const [data, setData] = useState<DeliveryNoteSearchResultDTO[]>([]);
    const [loading, setLoading] = useState(false);
    const [pageCount, setPageCount] = useState(1);
    const [searchParams] = useSearchParams();
    const [selectedTechnician, setSelectedTechnician] = useState<string>(Utilities.LoadParameterFromURLQuery("technician", "string", ""));
    const [techninians, setTechnicians] = useState<SelectItemsProps[]>([]);
    
    const [page] = useState<number>(Utilities.LoadPageFromURLQuery("page"));
    const [pageSize] = useState<number>(
        Utilities.LoadPageSizeFromURLQuery(pageSizes)
    );

    //Search Parameters
    const [atCode, setATCode] = useState<string>(Utilities.LoadParameterFromURLQuery("atCode", "string", ""))

    const [currentPage, setCurrentPage] = useState<number>();
    const [currentPageSize, setCurrentPageSize] = useState<number>();
    const [currentSorting, setCurrentSorting] = useState<
        SortingRule<object>[] | null
    >(null);
    const [goToPage, setGoToPage] = useState<number>();
    const [initialSortBy] = useState<SortingRule<object>[]>(
        Utilities.LoadSortParametersFromURLQuery("sortField", "sortOrder")
    );
    const [reloadSearch, setReloadSearch] = useState<boolean>(false);
    


    const columns = React.useMemo(
        () => [
            {
                Header: <FormattedMessage id="Screens.DeliveryNotes.Table.Column1" />,
                accessor: "atCode",
                width: 10,
            },
            
            {
                Header: <FormattedMessage id="Screens.DeliveryNotes.Table.Column2" />,
                accessor: "date",
                width: 10,
                Cell: (props: any) => {
                    return <span>{Utilities.formatDate(new Date(props.cell.row.original.date))}</span>;
                },
            },
            {
                Header: <FormattedMessage id="Screens.DeliveryNotes.Table.Column3" />,
                accessor: "shippingAddress",
                width: 10,
            },
            {
                Header: <FormattedMessage id="Screens.DeliveryNotes.Table.Column4" />,
                accessor: "deliveryAddress",
                width: 10,
            },
            {
                Header: <FormattedMessage id="Screens.DeliveryNotes.Table.Column5" />,
                accessor: "description",
                width: 40,
            },
            {
                Header: <FormattedMessage id="Screens.DeliveryNotes.Table.Column6" />,
                accessor: "technician",
                width: 10,
            },
            {
                Header: <FormattedMessage id="Screens.DeliveryNotes.Table.Column7" />,
                accessor: "assistance",
                width: 10,
            }
        ],
        []
    );

    const fetchData = async (page: number, pageSize: number, sortBy: SortingRule<object>[] | null) => 
        {
        //Atualizamos na history do browser a current page e pageSize
        var searchUrl: string = Utilities.CreateSearchURL([
            `page=${page}`,
            `pageSize=${pageSize}`,
            Utilities.AddParamToQueryIfValue("atCode", "string", atCode),
            Utilities.AddParamToQueryIfValue("technician", "string", selectedTechnician),
        ]);

        if (sortBy != null && sortBy.length > 0) {
            searchUrl += `${Utilities.AddParamToQueryIfValue(
                "sortField",
                "string",
                sortBy[0].id
            )}${Utilities.AddParamToQueryIfValue(
                "sortOrder",
                "string",
                sortBy[0].desc === true ? "DESC" : "ASC"
            )}`;
        }
        
        //Se antes não existirem parâmetros de pesquisa, significa que o user veio para aqui de uma rota, então para não ficar com duas rotas duplicadas, fazemos replace à rota

        
        if (searchParams.toString() === "") {
            navigate(`/deliveryNotes${searchUrl}`, { replace: true });
        } else {
            //Se já existirem parâmetros de pesquisa, não fazemos replace mas push
            navigate(`/deliveryNotes${searchUrl}`);
        }

        setLoading(true);

        //Vamos obter os dados

        var response = await deliveryNoteService.GetAll(
            mountSearchParameters(),
            Utilities.MountSortParameters(sortBy),
            page,
            pageSize
        );
        if (response.success !== true) {
            setData([]);
            setPageCount(0);
            setLoading(false);
            setCurrentPageSize(5);
            setCurrentPage(1);
            setCurrentSorting(null);
            Toast.Show("error", response.message);
            return;
        }

        //Atribuímos os dados
        setData(response.items);
        setPageCount(response.totalPages);
        setLoading(false);
        setCurrentPageSize(pageSize);
        setCurrentPage(page);
        setCurrentSorting(sortBy);
        setGoToPage(0);
        

        //Se a pesquisa tiver retornado múltiplas páginas e a página atual for superior ao nº de páginas que chegaram mandamos o user para a página 1
        if (response.totalPages > 0 && page > response.totalPages) {
            setGoToPage(1);
        }
    };

    const getTechnicians = async () => {
        const list: SelectItemsProps[] = [];
        var response = await assistanceService.GetWorkshopTechniciansWithCode();

        if (response.success === false) {
            Toast.Show("error", response.message);
            return;
        }

        response.obj.forEach((element) => {
            var item: SelectItemsProps = {
                value: element.id,
                label: element.name,
            };
            list.push(item);
        });

        setTechnicians(list);
    };

    //Método para montar os parâmetros de pesquisa
    const mountSearchParameters = (): Parameter[] => {
        var parameters: Parameter[] = [];
        parameters.push(new Parameter("atCode", atCode));
        parameters.push(new Parameter("technician", selectedTechnician));

        return parameters;
    };

    const onEnter = () => {
        fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
    };
    const onButtonClick = async () => {
        setCurrentPage(1);
        setGoToPage(1);
        fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
    };
    const onRemoveFiltersClick = async () => {
        setCurrentPage(1);
        setGoToPage(1);
        setATCode("");
        setSelectedTechnician("");
        setReloadSearch(true);
    };

    useEffect(() => {
        if (reloadSearch === true) {
            fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
            setReloadSearch(false);
        }
    }, [reloadSearch]);

    useEffect(() => {
        getTechnicians();
    }, []);

    const onNewTaskClick = () => {
        navigate(Routes.deliveryNotesUnsubmitted.list);
    };

    return (
        <>
            <Layout>
                <div className="pageContainer">
                    <div className="pageHeader">
                        <span className="pageTitle">
                            <FormattedMessage id="Screens.DeliveryNotes.Title" />
                            <ButtonComponent
                                    text={intl.formatMessage({ id: "Screens.DeliveryNotes.Button.UnsubmitedDeliveryNotes" })}
                                    onClick={onNewTaskClick}
                                    disabled={false} />
                        </span>
                    </div>

                    <div className="pageBody">
                        <Row>
                            <InputComponent
                                xl={2}
                                lg={3}
                                md={5}
                                sm={12}
                                xs={12}
                                style={{ width: "100%" }}
                                type={"text"}
                                onEnter={onEnter}
                                value={atCode}
                                label={intl.formatMessage({ id: "Screens.DeliveryNotes.SearchField1" })}
                                readOnly={false}
                                onChange={(t: string) => setATCode(t)}
                            />

                            <SelectComponent
                            xl={2}
                            lg={3}
                            md={5}
                            sm={6}
                            xs={12}
                            style={{ width: "100%", height: "43px" }}
                            label={"Técnico"}
                            selectedItemValue={selectedTechnician}
                            items={techninians}
                            onChange={(t: string) => {
                            setSelectedTechnician(t);
                            }}
                        />
                    </Row>
                        <Row>
                            <Col
                                xl={6}
                                lg={6}
                                md={6}
                                sm={12}
                                xs={12}
                                style={{ marginTop: "1em", display: "inline-flex", gap: "1rem" }}
                            >
                                <ButtonComponent
                                    text={intl.formatMessage({ id: "Common.Buttons.Search" })}
                                    onClick={onButtonClick}
                                    style={{ minWidth: "100px" }}
                                />
                                <ButtonComponent
                                    text={intl.formatMessage({ id: "Common.Buttons.RemoveFilters" })}
                                    onClick={onRemoveFiltersClick}
                                    style={{ minWidth: "113px" }}
                                />
                            </Col>
                        </Row>

                        <div className="pageComponent" style={{ width: "100%" }}>
                            <TableComponent
                                goToPage={goToPage}
                                loading={loading}
                                pageCount={pageCount}
                                columns={columns}
                                manualPagination={true}
                                fetchData={fetchData}
                                pageSizes={pageSizes}
                                initialPage={page}
                                initialPageSize={pageSize}
                                initialSortBy={initialSortBy}
                                data={data}
                                dontOrderFirstCollumn={true}
                            />
                        </div>
                    </div>
                </div>
            </Layout>
        </>
    );
    

}

